import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AppRole, Person } from "@somasi/shared";
import { ADMIN_ROLES, PAYROLL_READ_ROLES, PAYROLL_WRITE_ROLES, ROLE_LABELS } from "@somasi/shared";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { EMPLOYMENT_LABEL } from "@/components/person-form";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { KeyRound, Mail, Pencil, Plus, Trash2, UserPlus, UserX } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/billing";
import {
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useListSort,
  usePagination,
  type SortState,
} from "@/components/list-controls";

export const Route = createFileRoute("/_authenticated/people/")({
  head: () => ({
    meta: [
      { title: "People — Somasi Ops" },
      {
        name: "description",
        content:
          "Everyone who works here: records, contracts, sign-in accounts and role assignments.",
      },
    ],
  }),
  component: PeoplePage,
});

/**
 * Everyone who works here — one page, because one person.
 *
 * This replaces two screens. The employee register lived here and a "Users &
 * Roles" screen lived under Administration, over a table that shadowed the
 * Cognito user pool; between them they described one human being twice, and a
 * sweep existed to work out which rows on each were the same person. Here the
 * person is the record, the sign-in account is something they *have*, and
 * inviting one links it at birth.
 *
 * The screen belongs to the payroll roles. Below that line what each reader
 * sees is still decided by row rather than by screen — the payroll roles see
 * the terms, the administrative roles see and change the access — but a
 * colleague does not get in at all: this is a page about other people's pay,
 * contracts and system access, and there is no part of it they need.
 */
function PeoplePage() {
  const { hasAny, user: currentUser } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();

  /*
   * Three permissions, three sections, one page. Writing a record and granting
   * a role are deliberately different trusts: setting somebody's salary is the
   * payroll's job, and giving them the run of the system is not.
   *
   * `canWrite` now also decides the door. It is the same check the sidebar makes
   * before drawing the row, repeated here because a hidden link is not a
   * permission: the URL can still be typed, or arrive from a colleague.
   */
  const canWrite = hasAny(PAYROLL_WRITE_ROLES);
  const canSeeTerms = hasAny(PAYROLL_READ_ROLES);
  const canGrant = hasAny(ADMIN_ROLES);

  const [search, setSearch] = useState("");
  const text = search.trim().toLowerCase();
  const sort = useListSort({ column: "full_name", ascending: true });
  const pager = usePagination({ resetKey: { text, sort: sort.sort } });

  /*
   * The register is read whole and searched, sorted and paged here. The
   * endpoint can cut a page but cannot search or order one: the email searched
   * on lives on the account rather than the record, and the "unaccounted for"
   * accounts in the same envelope are only right for the whole pool. Read once,
   * too — the search used to be in the query key, which re-read the register on
   * every keystroke to filter the same answer.
   */
  const people = useQuery({
    queryKey: ["people"],
    // Not asked for at all by somebody who may not see the answer.
    enabled: canWrite,
    queryFn: async () => {
      const result = await api.employees.people();
      if (result.error) throw new Error(result.error.message);
      return result;
    },
  });

  const matching = useMemo(() => {
    const all = people.data?.people ?? [];
    const matches = (person: Person) =>
      !text ||
      [person.full_name, person.employee_no, person.job_title, person.account?.email]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(text));
    return all.filter(matches).sort(comparePeople(sort.sort));
  }, [people.data, text, sort.sort]);

  const rows = matching.slice(pager.offset, pager.offset + pager.pageSize);
  // Name, title, sign-in, access, type, week, status and actions, plus the terms.
  const columnCount = canSeeTerms ? 9 : 8;
  /** Accounts in the pool with no record behind them — the one state to clear. */
  const orphans = people.data?.unlinked ?? [];

  const refresh = () => void qc.invalidateQueries({ queryKey: ["people"] });

  const remove = useMutation({
    mutationFn: async (person: Person) => {
      const result = await api.employees.remove(person.id);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Record removed");
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const invite = useMutation({
    mutationFn: async (person: Person) => {
      const result = await api.employees.inviteAccount(person.id);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (account) => {
      notify.success(`Invitation sent to ${account?.email ?? "their address"}`);
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const revokeAccount = useMutation({
    mutationFn: async (person: Person) => {
      const result = await api.employees.removeAccount(person.id);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Sign-in removed; the record stays");
      refresh();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  if (!canWrite) {
    return (
      <div className="space-y-6">
        <PageHeader title="People" />
        <Alert variant="destructive">
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            Only the payroll roles may see the register. Your own days, leave and payslips are under
            Attendance, Leave and Payslips.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (people.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="People" />
        <Alert variant="destructive">
          <AlertTitle>Cannot read the register</AlertTitle>
          <AlertDescription>{(people.error as Error).message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="People"
          description="Everyone who works here — their record, their terms, and how they sign in."
          actions={
            canWrite ? (
              <Button asChild>
                <Link to="/people/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Add person
                </Link>
              </Button>
            ) : undefined
          }
        />

        {/*
         * The only loose end this design permits, so it is said plainly and it
         * closes. An account created from a person is linked in the same request;
         * what lands here is an account somebody signed themselves up for, or one
         * that predates the register. What it is missing is a record, so the fix
         * is to make one — not to match it to something.
         */}
        {canWrite && orphans.length > 0 && (
          <Alert>
            <KeyRound className="h-4 w-4" />
            <AlertTitle>
              {orphans.length} sign-in {orphans.length === 1 ? "account has" : "accounts have"} no
              record
            </AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                Somebody can sign in but is nobody here: no attendance, no leave, no payslip. Give
                each account the person it belongs to.
              </p>
              <div className="flex flex-wrap gap-2">
                {orphans.map((account) => (
                  <Button key={account.id} size="sm" variant="outline" asChild>
                    <Link to="/people/new" search={{ account: account.id }}>
                      <UserPlus className="mr-2 h-3.5 w-3.5" />
                      {account.email ?? account.id}
                    </Link>
                  </Button>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search by name, number, title or email…"
          />
        </ListToolbar>
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <SortHeader sort={sort} column="full_name">
                Name
              </SortHeader>
              <SortHeader sort={sort} column="job_title">
                Title
              </SortHeader>
              {/* Sign-in and access are the account's, joined on by the API, not
                  columns of the record. */}
              <TableHead>Sign-in</TableHead>
              <TableHead>Access</TableHead>
              <SortHeader sort={sort} column="employment_type">
                Type
              </SortHeader>
              {/* The contract's figure, which is a second row and not always sent. */}
              {canSeeTerms && <TableHead className="text-right">Annual gross</TableHead>}
              {/* A count of the working days, not a column that orders meaningfully. */}
              <TableHead className="text-center">Week</TableHead>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : (
              rows.length === 0 && (
                <TableMessageRow colSpan={columnCount}>
                  {text ? "Nobody matches that search." : "Nobody on the register yet."}
                </TableMessageRow>
              )
            )}

            {rows.map((person) => (
              <TableRow key={person.id}>
                <TableCell className="font-medium">
                  {/*
                   * The name is the link. A row that opened a modal could only
                   * be clicked; a page can be opened in a tab, linked to a
                   * colleague and come back to.
                   */}
                  <Link
                    to="/people/$personId"
                    params={{ personId: person.id }}
                    className="hover:underline"
                  >
                    {person.full_name}
                  </Link>
                  {person.employee_no && (
                    <span className="text-muted-foreground ml-2 text-xs">{person.employee_no}</span>
                  )}
                  {person.account?.id === currentUser?.id && (
                    <span className="text-muted-foreground ml-2 text-xs">(you)</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{person.job_title ?? "—"}</TableCell>
                <TableCell>
                  <AccountCell person={person} />
                </TableCell>
                <TableCell>
                  <RolesCell roles={person.roles} />
                </TableCell>
                <TableCell>{EMPLOYMENT_LABEL[person.employment_type]}</TableCell>
                {canSeeTerms && (
                  <TableCell className="text-right tabular-nums">
                    {person.contract ? (
                      money(person.contract.annual_gross)
                    ) : (
                      <span className="text-xs text-orange-700 dark:text-orange-400">no terms</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-center tabular-nums">
                  {person.working_days?.length ?? 5}d
                </TableCell>
                <TableCell>
                  <Badge variant={person.status === "active" ? "default" : "secondary"}>
                    {person.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {(canWrite || canGrant) && (
                    <RowActions>
                      <RowAction label="Open" asChild>
                        <Link to="/people/$personId" params={{ personId: person.id }}>
                          <Pencil />
                        </Link>
                      </RowAction>

                      {canWrite && !person.user_id && person.email && (
                        <RowAction
                          label="Invite to sign in"
                          disabled={invite.isPending}
                          onClick={() => invite.mutate(person)}
                        >
                          <Mail />
                        </RowAction>
                      )}

                      {canGrant && person.user_id && person.account?.id !== currentUser?.id && (
                        <RowAction
                          label="Remove sign-in"
                          onClick={async () => {
                            const ok = await confirmDelete(
                              `${person.full_name}'s sign-in`,
                              "The account and its roles go; the record stays, because their " +
                                "payslips are still records. They can be invited again later.",
                            );
                            if (ok) revokeAccount.mutate(person);
                          }}
                        >
                          <UserX />
                        </RowAction>
                      )}

                      {canWrite && (
                        <RowAction
                          label="Remove record"
                          destructive
                          onClick={async () => {
                            const ok = await confirmDelete(
                              person.full_name,
                              "Somebody who has been paid cannot be removed — their payslips are " +
                                "the record of money that moved. Set their status to “left” with " +
                                "an end date instead.",
                            );
                            if (ok) remove.mutate(person);
                          }}
                        >
                          <Trash2 />
                        </RowAction>
                      )}
                    </RowActions>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          pagination={pager}
          total={people.data ? matching.length : undefined}
          loading={people.isLoading}
          unit="person"
          units="people"
        />
      </ListPageTable>
    </ListPage>
  );
}

/**
 * The order the register is shown in, by one of the record's own columns.
 *
 * Blanks — somebody with no job title — go last whichever way it runs, since a
 * run of dashes at the top of the list says nothing. Ties fall back to the name,
 * so two people with the same title keep a stable order between pages.
 */
function comparePeople(sort: SortState) {
  const value = (person: Person) => {
    const raw = (person as unknown as Record<string, unknown>)[sort.column];
    return raw == null || raw === "" ? null : String(raw);
  };
  return (a: Person, b: Person) => {
    const left = value(a);
    const right = value(b);
    if (left !== right) {
      if (left == null) return 1;
      if (right == null) return -1;
      const diff = left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });
      if (diff !== 0) return sort.ascending ? diff : -diff;
    }
    return a.full_name.localeCompare(b.full_name, undefined, { sensitivity: "base" });
  };
}

/**
 * How this person signs in, in one cell.
 *
 * Four states, and only one of them is a problem. An account that has never
 * been used says so, because somebody waiting on an invitation they never
 * received looks exactly like somebody who is ignoring it. A deemed employee
 * has no account by design — a director drawing a fee never signs in — so "none"
 * against them is a fact rather than a gap; against anybody else it means an
 * empty attendance page and a payslip they cannot reach.
 */
function AccountCell({ person }: { person: Person }) {
  if (person.account) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{person.account.email ?? "linked"}</span>
        {!person.account.confirmed && (
          <span className="text-[10px] text-orange-700 dark:text-orange-400">
            invited, not signed in yet
          </span>
        )}
      </div>
    );
  }

  if (person.user_id) {
    // Linked to a subject the pool no longer has — an account deleted outside
    // this page. Worth saying, because it is not the same as having none.
    return (
      <Badge
        variant="outline"
        className="border-orange-400 text-[10px] text-orange-900 dark:border-orange-500/60 dark:text-orange-300"
      >
        Account missing
      </Badge>
    );
  }

  if (person.employment_type === "deemed_employee") {
    return <span className="text-muted-foreground text-xs">not needed</span>;
  }

  return (
    <Badge
      variant="outline"
      className="border-orange-400 text-[10px] text-orange-900 dark:border-orange-500/60 dark:text-orange-300"
    >
      No sign-in
    </Badge>
  );
}

/**
 * The roles an account holds, or the fact that it holds none.
 *
 * `roles` is defaulted rather than assumed. The API always sends the array, but
 * the browser and the API deploy separately, and a page loaded against an older
 * API that does not send it would otherwise take the whole table down on
 * `undefined.length` — which is a blank screen in place of a column.
 */
function RolesCell({ roles = [] }: { roles?: AppRole[] }) {
  if (roles.length === 0) {
    return <span className="text-muted-foreground text-xs">none</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <Badge key={role} variant="secondary" className="text-[10px]">
          {ROLE_LABELS[role]}
        </Badge>
      ))}
    </div>
  );
}

/**
 * One person, edited in one place.
 *
 * Three sections, each gated on what the reader is trusted with rather than on
 * which screen they found it from: the record, their terms, and their access.
 * It saves once — the server splits the payload back across the two rows the
 * person is stored in, so "move them to four days and give them a rise" is one
 * action and not two.
 *
 * `adopting` is the one odd entrance: an account that exists with nobody behind
 * it, which this creates the missing record for.
 */
